import {
  API_BASE_URL,
  apiDelete,
  apiGet,
  apiGetOptional,
  apiPatch,
  apiPost,
} from '@/api/http';

import type { EditingQuestion } from '@/components/form/shared/screening-questions/ScreeningQuestionInlineEditForm';
import type { CompanyUsersResponse } from '@/features/permissions/types';
import type { FeeTemplateAgencyCompany } from '@/types/agencyCompany';
import type {
  ClientCompanyDetail,
  ClientCompanySettings,
  ClientFeeTemplate,
  Company,
  CompanyDefaultJobExtensionField,
  CompanyDefaultJobExtensionFieldPayload,
  AdminClientCompany,
} from '@/types/company';
import type { ScreeningQuestionConfig } from '@/types/screeningQuestion';
import type { CursorListingApiPayload } from '@/UI/filteredListing';

export const getCompanyByName = (name: string) =>
  apiGetOptional<Company[]>(`${API_BASE_URL}client-companies/?search=${name}`);

export const getAdminClientCompanyList = (query?: string) =>
  apiGet<CursorListingApiPayload<AdminClientCompany>>(
    `${API_BASE_URL}client-companies/${query ?? ''}`,
  );
export const getClientCompanyById = (id: string) =>
  apiGetOptional<ClientCompanyDetail>(`${API_BASE_URL}client-companies/${id}/`);

export const getRecruiterCompanyByName = (name: string) =>
  apiGetOptional<{ id: string; name: string }[]>(
    `${API_BASE_URL}recruiter-companies/?search=${name}`,
  );

type CompanyDepartmentsPayload = { departments?: string[] };

const normalizeCompanyDepartments = (
  data: CompanyDepartmentsPayload | string[] | undefined,
): string[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.departments ?? [];
};

export const getClientCompanyDepartments = async (
  id: string,
): Promise<string[] | undefined> => {
  const data = await apiGetOptional<CompanyDepartmentsPayload | string[]>(
    `${API_BASE_URL}client-companies/${id}/departments/`,
  );
  if (!data) return undefined;
  return normalizeCompanyDepartments(data);
};

export const getCompanyFeeTemplates = () =>
  apiGet<ClientFeeTemplate[]>(`${API_BASE_URL}client-company-fee-templates/`);

export const getCompanyFeeTemplatesPage = (params: URLSearchParams) =>
  apiGet<CursorListingApiPayload<ClientFeeTemplate>>(
    `${API_BASE_URL}client-company-fee-templates/?${params.toString()}`,
  );

export const getCompanyFeeTemplate = (id: string) =>
  apiGet<ClientFeeTemplate>(
    `${API_BASE_URL}client-company-fee-templates/${id}/`,
  );

export type CompanyFeeTemplatePayload =
  FormData | Partial<Omit<ClientFeeTemplate, 'id' | 'client'>>;

export const createCompanyFeeTemplate = (data: CompanyFeeTemplatePayload) =>
  apiPost<ClientFeeTemplate>(
    `${API_BASE_URL}client-company-fee-templates/`,
    data,
  );

export const updateCompanyFeeTemplate = (
  id: string,
  data: CompanyFeeTemplatePayload,
) =>
  apiPatch<ClientFeeTemplate>(
    `${API_BASE_URL}client-company-fee-templates/${id}/`,
    data,
  );

export const deleteCompanyFeeTemplate = (id: string) =>
  apiDelete(`${API_BASE_URL}client-company-fee-templates/${id}/`);

export type FeeTemplateAgencyRow = {
  recruiter_id: string;
  agency_id: string;
  agency_name: string;
  agency_image: string | null;
  recruiter_name: string;
  recruiter_email: string;
  is_assigned: boolean;
  agreement_status: 'accepted' | 'pending' | 'declined' | 'none';
  originating_contract_name: string | null;
  agreement_id: string;
  accepted_date: string | null;
  compensation_type: string | null;
  working_types: string[];
  fee_currency: string | null;
};

export const getFeeTemplateAgenciesPage = (
  feeTemplateId: string,
  params: URLSearchParams,
) =>
  apiGet<CursorListingApiPayload<FeeTemplateAgencyRow>>(
    `${API_BASE_URL}client-company-fee-templates/${feeTemplateId}/agencies/?${params.toString()}`,
  );

export type FeeTemplateListRow = {
  list_id: string;
  name: string;
  description: string | null;
  recruiter_count: number;
  assigned_count: number;
  assignable_count: number;
  is_fully_assigned: boolean;
};

export const getFeeTemplateListsPage = (
  feeTemplateId: string,
  params: URLSearchParams,
) =>
  apiGet<CursorListingApiPayload<FeeTemplateListRow>>(
    `${API_BASE_URL}client-company-fee-templates/${feeTemplateId}/lists/?${params.toString()}`,
  );

export const getFeeTemplateAgencyCompaniesPage = (
  feeTemplateId: string,
  params: URLSearchParams,
) =>
  apiGet<CursorListingApiPayload<FeeTemplateAgencyCompany>>(
    `${API_BASE_URL}client-company-fee-templates/${feeTemplateId}/agency-companies/?${params.toString()}`,
  );

export const assignAgenciesToFeeTemplate = (
  feeTemplateId: string,
  data: {
    assign?: string[];
    assign_lists?: string[];
    assign_agency_companies?: string[];
    unassign_agency_companies?: string[];
  },
) =>
  apiPost(
    `${API_BASE_URL}client-company-fee-templates/${feeTemplateId}/assign-agencies/`,
    data,
  );

export const getClientCompanySettings = () =>
  apiGet<ClientCompanySettings>(
    `${API_BASE_URL}client-companies/current/settings/`,
  );

export const updateClientCompanySettings = (
  data: Partial<ClientCompanySettings>,
) => apiPatch(`${API_BASE_URL}client-companies/current/settings/`, data);

export const getClientCompanyHiringTeamRoles = () =>
  apiGet<{ roles: string[] }>(
    `${API_BASE_URL}client-companies/current/hiring-team-roles/`,
  );
type InviteEmailContentJson = object;

export interface ClientCompanyInviteEmailTemplatePlaceholder {
  token: string;
  label: string;
  description: string;
  sample: string;
}

export interface ClientCompanyInviteEmailTemplate {
  is_custom: boolean;
  language: string;
  template_language: string;
  subject: string;
  content_json: InviteEmailContentJson | null;
  content_html: string;
  content_text: string;
  default_subject: string;
  default_content_html: string;
  default_content_text: string;
  placeholders: ClientCompanyInviteEmailTemplatePlaceholder[];
}

export interface ClientCompanyInviteEmailTemplateUpdatePayload {
  subject: string;
  content_json?: InviteEmailContentJson | null;
  content_html: string;
  content_text?: string;
  language?: string;
}

export interface ClientCompanyInviteEmailTemplatePreview {
  subject: string;
  html: string;
  text: string;
}

export const getClientCompanyInviteEmailTemplate = (options?: {
  language?: string;
}) =>
  apiGet<ClientCompanyInviteEmailTemplate>(
    `${API_BASE_URL}client-companies/current/invite-email-template/`,
    options?.language ? { params: { language: options.language } } : undefined,
  );

export const updateClientCompanyInviteEmailTemplate = (
  data: ClientCompanyInviteEmailTemplateUpdatePayload,
) =>
  apiPatch<ClientCompanyInviteEmailTemplate>(
    `${API_BASE_URL}client-companies/current/invite-email-template/`,
    data,
  );

export const deleteClientCompanyInviteEmailTemplate = () =>
  apiDelete<ClientCompanyInviteEmailTemplate>(
    `${API_BASE_URL}client-companies/current/invite-email-template/`,
  );

export const previewClientCompanyInviteEmailTemplate = (
  data?: Partial<ClientCompanyInviteEmailTemplateUpdatePayload>,
) =>
  apiPost<ClientCompanyInviteEmailTemplatePreview>(
    `${API_BASE_URL}client-companies/current/invite-email-template/preview/`,
    data ?? {},
  );

export const getCompanyUsers = () =>
  apiGet<CompanyUsersResponse>(
    `${API_BASE_URL}client-companies/current/users/`,
  );

export const getCompanyDefaultJobExtensionFields = () =>
  apiGet<CompanyDefaultJobExtensionField[]>(
    `${API_BASE_URL}me/company-default-job-extension-fields/`,
  );

export const createCompanyDefaultJobExtensionField = (
  fieldData: CompanyDefaultJobExtensionFieldPayload,
) =>
  apiPost<CompanyDefaultJobExtensionField>(
    `${API_BASE_URL}me/company-default-job-extension-fields/`,
    fieldData,
  );

export const updateCompanyDefaultJobExtensionField = (
  fieldId: string,
  fieldData: Partial<CompanyDefaultJobExtensionFieldPayload>,
) =>
  apiPatch<CompanyDefaultJobExtensionField>(
    `${API_BASE_URL}me/company-default-job-extension-fields/${fieldId}/`,
    fieldData,
  );

export const deleteCompanyDefaultJobExtensionField = (fieldId: string) =>
  apiDelete(
    `${API_BASE_URL}me/company-default-job-extension-fields/${fieldId}/`,
  );

export const getCompanyDefaultJobExtensionFieldsForCompany = (
  companyId: string,
) =>
  apiGet<CompanyDefaultJobExtensionField[]>(
    `${API_BASE_URL}client-companies/${companyId}/default-job-extension-fields/`,
  );

export const getCompanyDefaultScreeningQuestions = () =>
  apiGet(`${API_BASE_URL}me/company-default-screening-questions/`);

export type CompanyDefaultScreeningQuestionUpdate = Partial<EditingQuestion> & {
  order?: number;
};

/**
 * Clearing a bound in the form leaves `null` or `''` behind, which the API stores
 * verbatim and later hands back as an unusable min/max. Omit those keys instead.
 */
const stripEmptyBounds = <
  T extends { validation_config?: ScreeningQuestionConfig },
>(
  questionData: T,
): T => {
  const { validation_config: config } = questionData;
  if (!config) return questionData;

  const sanitized: ScreeningQuestionConfig = { ...config };
  for (const key of ['min_value', 'max_value'] as const) {
    const value = sanitized[key];
    if (value === null || value === undefined || (value as unknown) === '') {
      delete sanitized[key];
    }
  }
  return { ...questionData, validation_config: sanitized };
};

export const createCompanyDefaultScreeningQuestion = (
  questionData: EditingQuestion,
) =>
  apiPost(
    `${API_BASE_URL}me/company-default-screening-questions/`,
    stripEmptyBounds(questionData),
  );

export const updateCompanyDefaultScreeningQuestion = (
  questionId: string,
  questionData: CompanyDefaultScreeningQuestionUpdate,
) =>
  apiPatch(
    `${API_BASE_URL}me/company-default-screening-questions/${questionId}/`,
    stripEmptyBounds(questionData),
  );

export const deleteCompanyDefaultScreeningQuestion = (questionId: string) =>
  apiDelete(
    `${API_BASE_URL}me/company-default-screening-questions/${questionId}/`,
  );
